<template>
	<div class="table-search">
		<span class="icon" aria-hidden="true">
			<AppIcon :name="IconName.Search" :size="14" :stroke-width="2" />
		</span>
		<input
			v-model="searchInput"
			ref="inputRef"
			type="text"
			class="input"
			:placeholder="placeholder"
			@focus="focused = true"
			@blur="focused = false"
			@keydown.escape="focused = false"
		/>
		<button
			v-if="searchInput"
			type="button"
			class="clear-button"
			:aria-label="t('search.clear')"
			@mousedown.stop.prevent="clear"
		>
			×
		</button>
		<div v-show="focused && suggestions.length > 0" class="suggestions">
			<div
				v-for="suggestion in suggestions"
				:key="suggestion"
				class="suggestion"
				@mousedown.prevent="select(suggestion)"
			>
				{{ suggestion }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

const props = defineProps<{
	modelValue: string
	placeholder?: string
	options?: string[]
}>()

const emit = defineEmits<{
	(e: "update:modelValue", value: string): void
}>()

const { t } = useI18n()

const focused = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

const searchInput = computed({
	get: () => props.modelValue,
	set: val => emit("update:modelValue", val),
})

const suggestions = computed(() => {
	const query = props.modelValue.trim().toLowerCase()
	if (!query) return []
	return (props.options ?? []).filter(opt => opt.toLowerCase().includes(query)).slice(0, 6)
})

function select(option: string) {
	emit("update:modelValue", option)
	focused.value = false
}

function clear() {
	emit("update:modelValue", "")
	inputRef.value?.focus()
}
</script>

<style scoped>
.table-search {
	position: relative;
	display: flex;
	align-items: center;

	.icon {
		position: absolute;
		left: var(--spacing-sm);
		display: flex;
		align-items: center;
		color: var(--neutral-600);
		pointer-events: none;
	}

	.input {
		width: 290px;
		height: var(--space-28);
		padding: 0 calc(var(--spacing-md) + var(--spacing-lg)) 0 calc(var(--spacing-sm) * 2 + var(--space-14));
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-full);
		font-size: var(--font-size-small);
		font-family: var(--font-family-primary);
		background: var(--neutral-0);
		color: var(--neutral-900);
		outline: none;
		box-sizing: border-box;

		&:focus {
			border-color: var(--neutral-900);
		}
	}

	.clear-button {
		position: absolute;
		right: var(--spacing-sm);
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--spacing-md);
		height: var(--spacing-md);
		border: none;
		background: var(--neutral-200);
		border-radius: 50%;
		cursor: pointer;
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		line-height: 1;
		transition: background-color 0.2s ease;

		&:hover {
			background: var(--neutral-400);
		}
	}

	.suggestions {
		position: absolute;
		top: calc(100% + var(--spacing-xxs));
		left: 0;
		right: 0;
		background: var(--neutral-0);
		border-radius: var(--border-radius-sm);
		box-shadow: var(--shadow-lg);
		overflow: hidden;
		z-index: 100;
	}

	.suggestion {
		padding: var(--spacing-sm) var(--spacing-md);
		font-size: var(--font-size-small);
		color: var(--neutral-600);
		cursor: pointer;

		&:not(:last-child) {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
		}

		&:hover {
			background: var(--neutral-200);
		}
	}
}
</style>
